import { createSelector, createStructuredSelector } from 'reselect';
import { EveryoneSegmentId } from '@fullstory/client-api';
import { isNonNull } from '@fullstory/typeutils';
import { AppState } from '../../types';
import { createDeepEqualSelector } from '@fullstory/redux-util/utils/selectors';
import { nameAndIdCompare } from './comparators';
import { getCustomSegmentKey, getCustomSegmentLabel } from './util';
import { SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import { Segment } from '@fullstory/proto/fs/services/search-settings';
import { pickBy } from 'lodash';
import { SegmentSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { SegmentDetailWithScope } from './types';

const RECENT_SEGMENTS_MAX_LENGTH = 10;

export const getSegmentById = (
  state: AppState['segments'],
  segmentId: string,
): SegmentDetail | undefined => state.segmentsById[segmentId];
export const getSegmentsAccess = (state: AppState) => state.segments.access;

export const getSegmentsById = (state: AppState) => state.segments.segmentsById;
export const getActiveSegmentsById = createSelector(getSegmentsById, segments =>
  pickBy(segments, segment => !segment.segment?.metadata?.archived),
);
const getInitialSegmentIds = (state: AppState) => state.segments.initialSegmentIds;
export const getSegmentsLoading = (state: AppState) => state.segments.allSegmentsRequest.loading;

interface SegmentsInfo {
  segmentsById: ReturnType<typeof getSegmentsById>;
  segmentsLoading: boolean;
}
export const getSegmentsInfo = createStructuredSelector<AppState, SegmentsInfo>({
  segmentsById: getSegmentsById,
  segmentsLoading: getSegmentsLoading,
});

export const getAllSegments = createSelector(getSegmentsById, segments =>
  Object.values(segments).sort(nameAndIdCompare),
);

export const getActiveSegments = createSelector(getActiveSegmentsById, segments =>
  Object.values(segments).sort(nameAndIdCompare),
);

export const getEveryoneSegment = createSelector(getSegmentsById, byId => byId && byId.everyone);

export const getSelectedSegmentId = (state: AppState) => state.segments.lastKnownSegment;

/**
 * Use this to get the currently selected segment. We can't always get the selected segment ID from
 * the URL because of /session routes, so we track the last known segment ID in Redux.
 */
export const getSelectedSegment = createSelector(
  getSegmentsById,
  getSelectedSegmentId,
  (segmentsById, lastKnownSegmentId) => segmentsById[lastKnownSegmentId],
);

export const getRecentSegments = createSelector(
  getInitialSegmentIds,
  getSegmentsById,
  (initialIds, byId): SegmentDetail[] => {
    // Filter recent segments to not include starred segments or the Everyone segment
    const recents = initialIds.data.recent.filter(
      recentId =>
        recentId != EveryoneSegmentId &&
        !initialIds.data.starred.find(starredId => starredId == recentId),
    );
    return (
      recents
        // slice before lookup + sorting so that we get the most recent RECENT_SEGMENTS_MAX_LENGTH segments
        .slice(0, RECENT_SEGMENTS_MAX_LENGTH)
        // then lookup full segment
        .map(d => byId[d])
        // filter out recents that are not found in the byId map (for TS purposes mainly)
        .filter(isNonNull)
        // and sort for easier viewing
        .sort(nameAndIdCompare)
    );
  },
);

export const getAllRecentSegments = createSelector(
  getInitialSegmentIds,
  getSegmentsById,
  (initialIds, byId): SegmentDetail[] => {
    const recents = initialIds.data.recent.filter(recentId => recentId != EveryoneSegmentId);
    return recents
      .map(d => byId[d])
      .filter(isNonNull)
      .sort(nameAndIdCompare);
  },
);

export const getStarredSegments = createSelector(
  getInitialSegmentIds,
  getSegmentsById,
  (data, byId) => data.data.starred.map((d: string) => byId[d]),
);

export const getFollowedSegments = createSelector(
  (state: AppState) => state.segments.followedSegments,
  segments => segments ?? [],
);

export const getDigestSegments = createSelector(
  getAllSegments,
  getFollowedSegments,
  (allSegments, followedSegments) =>
    allSegments
      .reduce((acc, segment) => {
        const s = segment.segment?.segment;
        if (s && followedSegments.includes(s?.id)) {
          acc.push(s);
        }
        return acc;
      }, [] as Segment[])
      .sort((s1, s2) => s1.name.localeCompare(s2.name)),
);

export const makeGetSegmentsFromSettings = () =>
  createDeepEqualSelector(
    getSegmentsById,
    (_: AppState, segmentSettings: SegmentSettings[]) => segmentSettings,
    (segmentsById, segmentSettings): SegmentDetailWithScope[] => {
      const customSegmentSettings = segmentSettings?.filter(({ segmentId }) => !segmentId);
      return segmentSettings
        ?.map(settings => {
          const { segmentId, segmentDefinition, namedSegmentDefinition, segmentScope } = settings;
          if (segmentId && segmentsById?.[segmentId]) {
            return {
              ...segmentsById[segmentId]!,
              segmentScope,
            };
          }
          const segDefinition = segmentDefinition || namedSegmentDefinition?.segmentDefinition;
          if (segDefinition) {
            const index =
              customSegmentSettings.length > 1
                ? customSegmentSettings.indexOf(settings)
                : undefined;
            return {
              segment: {
                segment: {
                  id: getCustomSegmentKey(index),
                  name: getCustomSegmentLabel(index, settings),
                  description: '',
                  segmentDefinition: segDefinition,
                },
                segmentMetadata: null,
                metadata: null,
              },
              starred: false,
              lastViewed: null,
              segmentScope,
            };
          }
          return null;
        })
        .filter(isNonNull);
    },
  );
